import {
  ALARM_CATEGORIES,
  ALARM_CATEGORY_LABELS,
  ALARM_RULE_LIST,
  ALARM_SEVERITIES,
  ALARM_SEVERITY_LABELS,
  ALARM_STATUS_LABELS,
  ALARM_STATUSES,
  type AlarmCategory,
  type AlarmListItem,
  type AlarmRuleCode,
  type Page,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Label,
  Select,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BellRing, Settings2 } from "lucide-react";
import { type ReactElement, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  type AlarmFilters,
  alarmListQuery,
  alarmScopeText,
  DEFAULT_ALARM_FILTERS,
  filtersFromSearch,
  isDefaultFilters,
  parseAlarmSearch,
  searchFromFilters,
  setCategory,
  setOpenOnly,
  toggleSeverity,
  toggleStatus,
} from "../../../features/alarms/alarm-values.js";
import {
  useAlarmList,
  useAlarmPermissions,
  useAlarmSummary,
  useStationOptions,
} from "../../../features/alarms/queries.js";
import {
  AlarmsPage,
  AlarmStatusBadge,
  RelativeTime,
  SeverityBadge,
  SeverityCountsRow,
} from "../../../features/alarms/shared.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

export const Route = createFileRoute("/_app/alarmlar/")({
  validateSearch: (search) => parseAlarmSearch(search as Record<string, unknown>),
  component: AlarmListRoute,
});

/**
 * Alarm listesi (Prompt 20 web, `GET /alarms` imleçli). Süzgeçler URL'dedir ve API ile aynı adları taşır — motorun grup
 * bildirimi `/alarmlar?stationId=…&category=…` doğrudan bu ekranı süzer. "Yalnızca açıklar" varsayılandır. Üstte açık
 * alarmların önem sayıları (`/alarms/summary`). Liste `alarms.*` olaylarıyla canlı tazelenir; tek istasyonlu kiracıda
 * istasyon seçici çizilmez.
 */
function AlarmListRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useAlarmPermissions();
  return (
    <AlarmsPage
      title={t("menu.alarms")}
      description={t("alarms.list.description")}
      actions={
        permissions.canSeeRules ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/alarmlar/kurallar">
              <Settings2 aria-hidden="true" />
              {t("menu.alarmRules")}
            </Link>
          </Button>
        ) : null
      }
    >
      <AlarmListBody />
    </AlarmsPage>
  );
}

function AlarmListBody(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const shape = useTenantShape();
  const filters = filtersFromSearch(search);
  const summary = useAlarmSummary();
  const stations = useStationOptions(!shape.isSingleStation);
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useAlarmList(filters, pages.cursor);
  const setFilters = (next: AlarmFilters) =>
    void navigate({ to: ".", search: searchFromFilters(next) as never, replace: true });

  const columns = useMemo<DataTableColumn<AlarmListItem>[]>(
    () => [
      {
        id: "severity",
        header: t("alarms.fields.severity"),
        exportValue: (row) => ALARM_SEVERITY_LABELS[row.severity],
        cell: (row) => <SeverityBadge severity={row.severity} />,
      },
      {
        id: "title",
        header: t("alarms.fields.alarm"),
        hideable: false,
        exportValue: (row) => row.title,
        cell: (row) => (
          <span className="grid min-w-56 gap-0.5">
            <Link
              to="/alarmlar/$alarmId"
              params={{ alarmId: row.id }}
              className="font-medium underline underline-offset-4"
            >
              {row.title}
            </Link>
            <span className="text-xs text-muted-foreground">{row.ruleLabel}</span>
          </span>
        ),
      },
      {
        id: "scope",
        header: t("alarms.fields.scope"),
        exportValue: (row) => alarmScopeText(row, t),
        cell: (row) => <span className="whitespace-nowrap">{alarmScopeText(row, t)}</span>,
      },
      {
        id: "firstOccurredAt",
        header: t("alarms.fields.firstOccurredAt"),
        exportValue: (row) => formatDateTime(row.firstOccurredAt),
        cell: (row) => (
          <span className="tabular whitespace-nowrap">{formatDateTime(row.firstOccurredAt)}</span>
        ),
      },
      {
        id: "lastOccurredAt",
        header: t("alarms.fields.lastOccurredAt"),
        exportValue: (row) => formatDateTime(row.lastOccurredAt),
        cell: (row) => (
          <span className="whitespace-nowrap">
            <RelativeTime value={row.lastOccurredAt} />
          </span>
        ),
      },
      {
        id: "occurrenceCount",
        header: t("alarms.fields.occurrenceCount"),
        align: "right",
        exportValue: (row) => row.occurrenceCount,
        cell: (row) => <span className="tabular">{formatCount(row.occurrenceCount)}</span>,
      },
      {
        id: "status",
        header: t("alarms.fields.status"),
        exportValue: (row) => ALARM_STATUS_LABELS[row.status],
        cell: (row) => (
          <span className="grid justify-items-start gap-1">
            <AlarmStatusBadge status={row.status} />
            {row.autoResolved && row.status === "RESOLVED" ? (
              <span className="text-xs text-muted-foreground">{t("alarms.autoResolved")}</span>
            ) : null}
            {row.escalationLevel > 0 && row.status !== "RESOLVED" ? (
              <span className="text-xs text-muted-foreground">
                {t("alarms.escalatedLevel", { level: row.escalationLevel })}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: "assignee",
        header: t("alarms.fields.assignee"),
        exportValue: (row) => row.assignee?.fullName ?? null,
        cell: (row) => <span className="whitespace-nowrap">{row.assignee?.fullName ?? "—"}</span>,
      },
    ],
    [t],
  );

  return (
    <>
      <Card className="grid gap-3 p-4">
        <h2 className="text-sm font-semibold">{t("alarms.summary.openTitle")}</h2>
        {summary.error ? (
          <Alert tone="danger">{errorMessage(summary.error)}</Alert>
        ) : summary.data ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <SeverityCountsRow counts={summary.data.open} />
            <span className="text-sm text-muted-foreground">
              {t("alarms.summary.total", { count: summary.data.open.total })}
            </span>
            {summary.data.assignedToMe > 0 ? (
              <Button
                variant="link"
                size="sm"
                className="h-auto px-0"
                onClick={() => setFilters({ ...DEFAULT_ALARM_FILTERS, assignedToMe: true })}
              >
                {t("alarms.summary.assignedToMe", { count: summary.data.assignedToMe })}
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        )}
      </Card>
      <AlarmFilterBar
        filters={filters}
        setFilters={setFilters}
        stations={shape.isSingleStation ? null : (stations.data ?? [])}
      />
      <DataTable
        caption={t("menu.alarms")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={
          list.error ? (
            <Alert tone="danger">
              <p>{errorMessage(list.error)}</p>
              {!isDefaultFilters(filters) ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setFilters(DEFAULT_ALARM_FILTERS)}
                >
                  {t("common.clearFilters")}
                </Button>
              ) : null}
            </Alert>
          ) : undefined
        }
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<BellRing />}
            title={filters.openOnly ? t("alarms.list.emptyOpenTitle") : t("alarms.list.emptyTitle")}
            description={t("alarms.list.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<AlarmListItem>(
            async (cursor) =>
              (await api.GET("/alarms", { params: { query: alarmListQuery(filters, cursor, 200) as never } }))
                .data! as Page<AlarmListItem>,
          );
          await exportRowsToExcel({
            fileName: "alarmlar",
            sheetName: t("menu.alarms"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}

function CheckboxGroup<T extends string>({
  legend,
  values,
  selected,
  label,
  onToggle,
}: {
  legend: string;
  values: readonly T[];
  selected: readonly T[];
  label: (value: T) => string;
  onToggle: (value: T, checked: boolean) => void;
}): ReactElement {
  const id = useId();
  return (
    <fieldset className="grid gap-1.5">
      <legend className="mb-1.5 text-sm font-medium">{legend}</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {values.map((value) => (
          <div key={value} className="flex items-center gap-2">
            <Checkbox
              id={`${id}-${value}`}
              checked={selected.includes(value)}
              onChange={(event) => onToggle(value, event.target.checked)}
            />
            <Label htmlFor={`${id}-${value}`} className="font-normal">
              {label(value)}
            </Label>
          </div>
        ))}
      </div>
    </fieldset>
  );
}

function AlarmFilterBar({
  filters,
  setFilters,
  stations,
}: {
  filters: AlarmFilters;
  setFilters: (next: AlarmFilters) => void;
  /** `null` = tek istasyonlu kiracı (seçici çizilmez). */
  stations: readonly { id: string; code: string; name: string }[] | null;
}): ReactElement {
  const { t } = useTranslation();
  const openOnlyId = useId();
  const assignedId = useId();
  const rules = ALARM_RULE_LIST.filter(
    (rule) => filters.category === null || rule.category === filters.category,
  );
  // Bağlantıyla gelen istasyon listede yoksa (ör. pasif) yine seçili görünsün.
  const stationMissing =
    stations !== null &&
    filters.stationId !== null &&
    !stations.some((station) => station.id === filters.stationId);
  return (
    <Card className="grid gap-4 p-4">
      <div className="flex flex-wrap gap-x-8 gap-y-4">
        <CheckboxGroup
          legend={t("alarms.fields.severity")}
          values={ALARM_SEVERITIES}
          selected={filters.severity}
          label={(value) => ALARM_SEVERITY_LABELS[value]}
          onToggle={(value, checked) => setFilters(toggleSeverity(filters, value, checked))}
        />
        <CheckboxGroup
          legend={t("alarms.fields.status")}
          values={ALARM_STATUSES}
          selected={filters.status}
          label={(value) => ALARM_STATUS_LABELS[value]}
          onToggle={(value, checked) => setFilters(toggleStatus(filters, value, checked))}
        />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {stations !== null ? (
          <FormField label={t("alarms.fields.station")} className="w-full sm:w-60">
            {(control) => (
              <Select
                {...control}
                value={filters.stationId ?? ""}
                onChange={(event) => setFilters({ ...filters, stationId: event.target.value || null })}
              >
                <option value="">{t("alarms.list.allStations")}</option>
                {stationMissing ? (
                  <option value={filters.stationId!}>{t("alarms.list.linkedStation")}</option>
                ) : null}
                {stations.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.code} · {station.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
        <FormField label={t("alarms.fields.category")} className="w-full sm:w-60">
          {(control) => (
            <Select
              {...control}
              value={filters.category ?? ""}
              onChange={(event) =>
                setFilters(setCategory(filters, (event.target.value || null) as AlarmCategory | null))
              }
            >
              <option value="">{t("common.all")}</option>
              {ALARM_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {ALARM_CATEGORY_LABELS[category]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("alarms.fields.rule")} className="w-full sm:w-72">
          {(control) => (
            <Select
              {...control}
              value={filters.ruleCode ?? ""}
              onChange={(event) =>
                setFilters({ ...filters, ruleCode: (event.target.value || null) as AlarmRuleCode | null })
              }
            >
              <option value="">{t("common.all")}</option>
              {rules.map((rule) => (
                <option key={rule.code} value={rule.code}>
                  {rule.label}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id={openOnlyId}
            checked={filters.openOnly}
            onChange={(event) => setFilters(setOpenOnly(filters, event.target.checked))}
          />
          <Label htmlFor={openOnlyId} className="font-normal">
            {t("alarms.list.openOnly")}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={assignedId}
            checked={filters.assignedToMe}
            onChange={(event) => setFilters({ ...filters, assignedToMe: event.target.checked })}
          />
          <Label htmlFor={assignedId} className="font-normal">
            {t("alarms.list.assignedToMe")}
          </Label>
        </div>
        {!isDefaultFilters(filters) ? (
          <Button variant="ghost" size="sm" onClick={() => setFilters(DEFAULT_ALARM_FILTERS)}>
            {t("common.clearFilters")}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
